import { graphql } from '@/generated/gql'
import { ApolloError, useMutation, useQuery } from '@apollo/client'
import moment from 'moment'
import { useCallback, useEffect, useState } from 'react'

const useCcMenuUserDataQuery = graphql(`
  query canteenUserData {
    me {
      id
      ...CanteenMenu_UserDataFragment
      allergens {
        celery
        cereals
        crustaceans
        eggs
        fish
        lupin
        milk
        molluscs
        mustard
        nuts
        peanuts
        sesame
        soybeans
        sulphur
      }
      dietaryPreferences {
        vegan
        vegetarian
        halal
        kosher
        refinedSugar
      }
      upcomingCcMenus {
        date
        lunch {
          meal
          menuIsPublished
          openTime
          closeTime
        }
        dinner {
          meal
          menuIsPublished
          openTime
          closeTime
        }
        breakfast {
          meal
          menuIsPublished
          openTime
          closeTime
        }
      }
      wallet {
        balance
      }
      subsidySettings {
        unlimitedSubsidy
      }
      onboardingSettings {
        ccOnboarded
      }
      account {
        userInterfaceSettings {
          hideCCPrices
        }
        id
        name
        currency
        isCCActive
        topUpEnabled
      }
      accountLocation {
        id
        label
        timezone
        isCCActive
      }
    }
  }
`)

const useCcMenuUpdateLocationMutation = graphql(`
  mutation UseCcMenu_UpdateLocationMutation($accountLocationId: Int!) {
    updateMe(userPayload: { accountLocationId: $accountLocationId }) {
      accountLocation {
        id
        label
      }
    }
  }
`)

const useCcMenuUserStateUpdateOnboardedMutation = graphql(`
  mutation UseCcMenuUserState_UpdateOnboadedMutation {
    updateMe(userPayload: { onboardingSettings: { ccOnboarded: true } }) {
      id
    }
  }
`)

export enum CCMenuUserStateCustomErrorCode {
  NoTimezone = 'NO_TIMEZONE',
  SelectedLocationNotCCActive = 'USER_LOCATION_NOT_CC_ACTIVE',
  AccountNotCCActive = 'USER_ACCOUNT_NOT_CC_ACTIVE',
}

export type CCMenuUserStateCustomError = {
  message: string
  name: CCMenuUserStateCustomErrorCode
  redirect?: boolean
}

export const useCcMenuUserState = () => {
  const {
    data: userData,
    called,
    loading: loadingUserData,
    called: userDataCalled,
    refetch: refetchUserData,
    error: graphqlError,
  } = useQuery(useCcMenuUserDataQuery)

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<ApolloError | CCMenuUserStateCustomError | null>(null)
  const [runUpdateLocationMutation] = useMutation(useCcMenuUpdateLocationMutation)
  const [runUpdateOnboardedMutation] = useMutation(useCcMenuUserStateUpdateOnboardedMutation)

  useEffect(() => {
    if (!userData) return
    const timezone = userData.me.accountLocation?.timezone
    const accountLocation = userData.me.accountLocation
    const account = userData.me.account
    let error: CCMenuUserStateCustomError | null = null
    if (!timezone)
      error = { name: CCMenuUserStateCustomErrorCode.NoTimezone, message: 'No timezone' }
    if (accountLocation && !accountLocation?.isCCActive)
      error = {
        name: CCMenuUserStateCustomErrorCode.SelectedLocationNotCCActive,
        message: 'Chosen location not active for Cloud canteen',
        redirect: true,
      }
    if (account && !account?.isCCActive)
      error = {
        name: CCMenuUserStateCustomErrorCode.AccountNotCCActive,
        message: 'Your account is not active for Cloud canteen',
        redirect: true,
      }
    if (error) {
      setError(error)
      return
    }
    moment.tz.setDefault(timezone)
    if (!userData.me.onboardingSettings.ccOnboarded) runUpdateOnboardedMutation()
    setLoading(false)
  }, [userData, runUpdateOnboardedMutation])

  useEffect(() => {
    if (graphqlError) setError(graphqlError)
    if (
      graphqlError?.message ===
      '/grpc.accounts.v1.AccountsService/GetAccountLocation INVALID_ARGUMENT: Invalid request'
    ) {
      const account = userData?.me.account
      if (account && !account?.isCCActive) {
        setError({
          name: CCMenuUserStateCustomErrorCode.AccountNotCCActive,
          message: 'Your account is not active for Cloud canteen',
          redirect: true,
        })
      } else {
        setError({
          name: CCMenuUserStateCustomErrorCode.SelectedLocationNotCCActive,
          message: 'Chosen location not active for Cloud canteen',
          redirect: true,
        })
      }
    }
  }, [graphqlError])

  const updateAccountLocation = async (locationId: number) => {
    await runUpdateLocationMutation({ variables: { accountLocationId: locationId } })
    refetchUserData()
  }

  const handleRefetch = useCallback(async () => {
    setLoading(true)
    await refetchUserData()
  }, [refetchUserData])

  return {
    loading: loading || loadingUserData || !userDataCalled,
    data: userData?.me,
    called,
    error: error ?? graphqlError,
    refetch: handleRefetch,
    updateAccountLocation,
  }
}
